import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/ui-bits";
import { computeClientEstimate, formatCurrency, blankInputs } from "../lib/mmsPricing";
import {
  Save, Send, Trash2, FileText, Loader2, RotateCcw, Info,
  Sparkles, Image as ImageIcon, AlertTriangle, Check, MessagesSquare,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Primitives                                                         */
/* ------------------------------------------------------------------ */

function StepHeader({ number, title, hint }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-4">
        <div className="w-8 h-8 border border-[#0A0A0A] flex items-center justify-center text-xs">{number}</div>
        <h2 className="h-editorial text-xl">{title}</h2>
      </div>
      {hint && <div className="text-[11px] text-[#737373] mt-2 ml-12 leading-relaxed">{hint}</div>}
    </div>
  );
}
function Card({ children, ...rest }) { return <section className="card-dark p-6 md:p-8 mb-6" {...rest}>{children}</section>; }
function FieldLabel({ children }) { return <label className="label-caps block mb-2">{children}</label>; }
function EmptyHint({ what }) {
  return <div className="text-[11px] text-[#737373] italic border border-dashed border-[#E5E5E5] p-3">No {what} configured yet — an MMS admin needs to add them.</div>;
}

function ConfField({ children, path, confidence, warn, block, confirmed, onConfirm }) {
  if (confidence === undefined || confidence === null) return <>{children}</>;
  const conf = Number(confidence) || 0;
  const isConfirmed = confirmed?.includes(path);
  if (conf >= warn || isConfirmed) return <>{children}</>;
  const isBlank = conf < block;
  return (
    <div className={`relative border-l-2 pl-3 ${isBlank ? "border-red-500" : "border-amber-400"}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`text-[9px] uppercase tracking-widest inline-flex items-center gap-1 ${isBlank ? "text-red-600" : "text-amber-700"}`}
              data-testid={`mms-conf-badge-${path.replace(/[.\[\]]/g, "-")}`}>
          <AlertTriangle size={10} />
          {isBlank ? "Da selezionare" : "Da confermare"}
        </span>
        {!isBlank && (
          <button type="button" onClick={() => onConfirm(path)}
                  className="text-[9px] uppercase tracking-widest text-[#0A0A0A] hover:text-emerald-700 inline-flex items-center gap-1"
                  data-testid={`mms-conf-confirm-${path.replace(/[.\[\]]/g, "-")}`}>
            <Check size={10} /> Confirm
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AI Image Analyzer                                                  */
/* ------------------------------------------------------------------ */

function AiImageAnalyzer({ onResult }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [label, setLabel] = useState(null);
  const submit = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { setErr("Please upload a JPEG, PNG or WEBP image."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/mms/ai-analyze-image", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
      });
      setPreview(URL.createObjectURL(file));
      setLabel(data.confidence_label || null);
      onResult(data);
      toast.success("AI analysis complete");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-dark p-6 md:p-8 mb-8 bg-[#FAFAFA]" data-testid="mms-ai-analyzer">
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Sparkles size={16} strokeWidth={1.5} /><div className="label-caps">AI Garment Analyst</div></div>
          <h3 className="h-editorial text-xl mb-3">Let the MMS AI decompose your reference photo</h3>
          <p className="text-xs text-[#525252] mb-4 leading-relaxed">
            Upload a garment photo — the AI identifies the piece and its construction details, and MMS Studio
            returns an indicative commercial estimate. Fields the AI is unsure about are highlighted below so
            you can confirm or correct them before requesting the official quote.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                    className="btn-gold inline-flex items-center gap-2 disabled:opacity-50" data-testid="mms-ai-upload-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
              {busy ? "Analysing…" : "Analyze Image with AI"}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
                   onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; submit(f); }}
                   data-testid="mms-ai-file-input" />
            {label && (
              <span className="text-[10px] uppercase tracking-widest text-[#525252]" data-testid="mms-ai-confidence-label">
                AI status: <span className="text-[#0A0A0A]">{label}</span>
              </span>
            )}
          </div>
          {err && <div className="text-red-600 text-xs mt-3">{err}</div>}
        </div>
        {preview && <img src={preview} alt="reference" className="w-40 h-40 object-cover border border-[#E5E5E5]" data-testid="mms-ai-preview" />}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

export default function MmsSimulatorPage() {
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savedSims, setSavedSims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [inputs, setInputs] = useState(blankInputs());
  const [name, setName] = useState("");
  const [currentSimId, setCurrentSimId] = useState(null);
  const [confidenceMap, setConfidenceMap] = useState({});
  const [confirmedFields, setConfirmedFields] = useState([]);
  const [thresholds, setThresholds] = useState({ warn: 0.7, block: 0.4 });
  const [confidenceLabel, setConfidenceLabel] = useState(null);
  // Server-issued opaque token from /mms/ai-analyze-image. Sent with the
  // next save so the backend attaches the (server-stored) AI analysis.
  const [analysisToken, setAnalysisToken] = useState(null);
  const openSimId = params.get("sim") || null;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mms/pricing-config");
        setConfig(data);
        setThresholds({ warn: Number(data?.ai?.confidence_warn ?? 0.7), block: Number(data?.ai?.confidence_block ?? 0.4) });
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      finally { setLoadingConfig(false); }
      try { const { data } = await api.get("/mms/simulations"); setSavedSims(data); } catch { /* non-blocking */ }
    })();
  }, []);

  useEffect(() => {
    if (!openSimId) return;
    (async () => {
      try {
        const { data } = await api.get(`/mms/simulations/${openSimId}`);
        setInputs({ ...blankInputs(), ...data.inputs });
        setName(data.name || ""); setCurrentSimId(data.id);
        setConfidenceMap(data.inputs?.confidence || {}); setConfirmedFields(data.inputs?.confirmed_fields || []);
        setAnalysisToken(null);
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [openSimId]);

  const estimate = useMemo(() => (config ? computeClientEstimate(inputs, config) : null), [inputs, config]);

  const patch = (path, val) => setInputs((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = cur[keys[i]] || {}; cur = cur[keys[i]]; }
    cur[keys[keys.length - 1]] = val;
    return next;
  });

  const editWithConfirm = (path, val) => {
    patch(path, val);
    if (confidenceMap[path] !== undefined && !confirmedFields.includes(path)) setConfirmedFields((c) => [...c, path]);
  };

  const confirmField = (path) => setConfirmedFields((c) => c.includes(path) ? c : [...c, path]);

  const applyAiResult = (data) => {
    setInputs({
      ...blankInputs(),
      ...data.prefilled_inputs,
      // Only keep the thumbnail (which the client already uploaded). Raw
      // AI analysis / notes stay server-side; the backend re-attaches them
      // when we save via the analysis_token.
      ai_analysis: data.image_thumbnail_b64
        ? { image_thumbnail_b64: data.image_thumbnail_b64, image_thumbnail_mime: "image/jpeg" }
        : null,
      confidence: data.confidence_map,
      confirmed_fields: [],
    });
    setConfidenceMap(data.confidence_map || {});
    setConfirmedFields([]);
    setConfidenceLabel(data.confidence_label || null);
    setAnalysisToken(data.analysis_token || null);
  };

  const pendingConfirmations = useMemo(() => {
    const out = [];
    for (const [path, conf] of Object.entries(confidenceMap || {})) {
      const c = Number(conf) || 0;
      if (c < thresholds.warn && c >= thresholds.block && !confirmedFields.includes(path)) out.push(path);
    }
    return out;
  }, [confidenceMap, confirmedFields, thresholds]);

  const resetForm = () => {
    setInputs(blankInputs()); setName(""); setCurrentSimId(null);
    setConfidenceMap({}); setConfirmedFields([]);
    setConfidenceLabel(null); setAnalysisToken(null);
    setParams({});
  };

  const doSave = async () => {
    if (!config) return;
    setBusy(true); setErr("");
    try {
      const payload = {
        name: name || undefined,
        inputs: { ...inputs, confidence: confidenceMap, confirmed_fields: confirmedFields },
        ...(analysisToken ? { analysis_token: analysisToken } : {}),
      };
      if (currentSimId) {
        const { data } = await api.put(`/mms/simulations/${currentSimId}`, payload);
        setSavedSims((s) => s.map((x) => x.id === data.id ? data : x));
        toast.success("Simulation updated");
      } else {
        const { data } = await api.post("/mms/simulations", payload);
        setSavedSims((s) => [data, ...s]); setCurrentSimId(data.id);
        toast.success("Simulation saved");
      }
      setAnalysisToken(null);  // token is single-use once attached.
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doRequestQuote = async () => {
    if (!config) return;
    if (pendingConfirmations.length > 0) {
      toast.error(`Confirm ${pendingConfirmations.length} AI-flagged field${pendingConfirmations.length > 1 ? "s" : ""} first`);
      return;
    }
    setBusy(true); setErr("");
    try {
      let simId = currentSimId;
      const payload = {
        name: name || undefined,
        inputs: { ...inputs, confidence: confidenceMap, confirmed_fields: confirmedFields },
        ...(analysisToken ? { analysis_token: analysisToken } : {}),
      };
      if (!simId) {
        const { data } = await api.post("/mms/simulations", payload);
        simId = data.id; setCurrentSimId(simId); setSavedSims((s) => [data, ...s]);
      } else {
        await api.put(`/mms/simulations/${simId}`, payload);
      }
      const { data: updated } = await api.post(`/mms/simulations/${simId}/request-quote`);
      setSavedSims((s) => s.map((x) => x.id === updated.id ? updated : x));
      setAnalysisToken(null);
      toast.success("Quote request sent to MMS Studio");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const removeSim = async (id) => {
    if (!window.confirm("Delete this simulation?")) return;
    try {
      await api.delete(`/mms/simulations/${id}`);
      setSavedSims((s) => s.filter((x) => x.id !== id));
      if (currentSimId === id) resetForm();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (loadingConfig) return <div className="flex items-center gap-2 text-xs text-[#737373]"><Loader2 size={14} className="animate-spin" /> Loading price simulator…</div>;

  const taxonomy = config?.taxonomy || [];
  const modifiers = config?.construction_modifiers || [];
  const materialHandling = config?.material_handling || [];
  const complexityLevels = config?.complexity_levels || [];
  const patternSources = config?.pattern_sources || [];
  const manualBrackets = config?.manual_work_brackets || [];
  const processing = config?.processing_techniques || [];

  const selectedCatKey = inputs.garment.primary_category;
  const subcategoriesForCat = (taxonomy.find((c) => c.key === selectedCatKey) || {}).subcategories || [];
  const selectedSubcat = subcategoriesForCat.find((s) => s.key === inputs.garment.subcategory);

  const wrap = (path, node) => (
    <ConfField path={path} confidence={confidenceMap[path]} warn={thresholds.warn} block={thresholds.block}
               confirmed={confirmedFields} onConfirm={confirmField}>{node}</ConfField>
  );

  // --- Selected modifiers helper -----
  const isModSelected = (key) => (inputs.construction.modifiers || []).some((m) => m.key === key);
  const toggleModifier = (key) => setInputs((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const arr = next.construction.modifiers || [];
    const idx = arr.findIndex((m) => m.key === key);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push({ key, count: 1 });
    next.construction.modifiers = arr;
    if (confidenceMap[`construction.modifiers.${key}`] !== undefined && !confirmedFields.includes(`construction.modifiers.${key}`)) {
      setConfirmedFields((c) => [...c, `construction.modifiers.${key}`]);
    }
    return next;
  });
  const setModifierCount = (key, count) => setInputs((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const m = (next.construction.modifiers || []).find((x) => x.key === key);
    if (m) m.count = Math.max(1, Number(count) || 1);
    return next;
  });

  const isProcSelected = (key) => (inputs.processing || []).some((p) => p.key === key);
  const toggleProc = (key) => setInputs((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const arr = next.processing || [];
    const idx = arr.findIndex((p) => p.key === key);
    if (idx >= 0) arr.splice(idx, 1); else arr.push({ key });
    next.processing = arr;
    return next;
  });

  const isManualSelected = (key) => (inputs.manual_work || []).some((m) => m.key === key);
  const toggleManual = (key) => setInputs((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const arr = next.manual_work || [];
    const idx = arr.findIndex((m) => m.key === key);
    if (idx >= 0) arr.splice(idx, 1); else arr.push({ key });
    next.manual_work = arr;
    return next;
  });

  return (
    <div data-testid="mms-simulator-manual-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <PageHeader
            eyebrow="MMS Studio · Modalità manuale"
            title="MMS Price Simulator"
            description="Compila manualmente le sezioni del capo per generare il preventivo indicativo. Se preferisci, puoi tornare alla modalità AI e caricare direttamente una foto."
          />
        </div>
        <Link to="/mms-simulator"
              className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 whitespace-nowrap mt-4"
              data-testid="mms-back-to-chat-link">
          <MessagesSquare size={11} /> Torna alla modalità AI
        </Link>
      </div>

      <div className="mb-8 p-4 border border-[#E5E5E5] bg-[#FAFAFA] flex items-start gap-3">
        <Info size={14} className="text-[#525252] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[#525252] leading-relaxed">
          The prices generated by this simulator are indicative and do not constitute a final quotation.
          The final price will be confirmed by the MMS Studio team after reviewing the project.
        </p>
      </div>

      <AiImageAnalyzer onResult={applyAiResult} />
      {err && <div className="mb-6 border border-red-600/40 bg-red-50 text-red-700 text-xs p-3">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="mb-6">
            <FieldLabel>Simulation name</FieldLabel>
            <input data-testid="mms-sim-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. SS26 Capsule — Biker Jacket" className="input-dark w-full" />
          </div>

          {/* 1. Garment */}
          <Card data-testid="mms-step-garment">
            <StepHeader number={1} title="Garment classification" hint="Category, subcategory and complexity help MMS Studio understand the piece." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Primary category</FieldLabel>
                {taxonomy.length === 0 ? <EmptyHint what="taxonomy" /> : wrap("garment.primary_category",
                  <select className="input-dark w-full" value={inputs.garment.primary_category}
                          onChange={(e) => { editWithConfirm("garment.primary_category", e.target.value); patch("garment.subcategory", ""); }}
                          data-testid="mms-garment-category">
                    <option value="">— Select —</option>
                    {taxonomy.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                )}
              </div>
              <div>
                <FieldLabel>Subcategory</FieldLabel>
                {wrap("garment.subcategory",
                  <select className="input-dark w-full" value={inputs.garment.subcategory}
                          onChange={(e) => editWithConfirm("garment.subcategory", e.target.value)}
                          disabled={!selectedCatKey} data-testid="mms-garment-subcategory">
                    <option value="">— Select —</option>
                    {subcategoriesForCat.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                )}
              </div>
              <div>
                <FieldLabel>Fit</FieldLabel>
                {wrap("garment.fit",
                  <div className="grid grid-cols-4 gap-2">
                    {["slim", "regular", "relaxed", "oversize"].map((f) => (
                      <button key={f} type="button" onClick={() => editWithConfirm("garment.fit", f)}
                              className={`text-xs uppercase tracking-widest py-2 border ${inputs.garment.fit === f ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                              data-testid={`mms-fit-${f}`}>{f}</button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <FieldLabel>Complexity level</FieldLabel>
                {wrap("garment.complexity",
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {complexityLevels.map((c) => (
                      <button key={c.key} type="button" onClick={() => { editWithConfirm("garment.complexity", c.key); patch("production.complexity", c.key); }}
                              className={`text-xs uppercase tracking-widest py-2 border ${inputs.garment.complexity === c.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                              data-testid={`mms-complexity-${c.key}`}>{c.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* 2. Pattern source */}
          <Card data-testid="mms-step-pattern">
            <StepHeader number={2} title="Pattern (cartamodello)" hint="Where does the pattern come from? This changes the development effort MMS Studio needs to invest." />
            {wrap("pattern.source",
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {patternSources.map((s) => (
                  <button key={s.key} type="button" onClick={() => patch("pattern.source", s.key)}
                          className={`text-left text-xs px-4 py-3 border ${inputs.pattern.source === s.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                          data-testid={`mms-pattern-source-${s.key}`}>{s.label}</button>
                ))}
              </div>
            )}
          </Card>

          {/* 3. Construction modifiers */}
          <Card data-testid="mms-step-construction">
            <StepHeader number={3} title="Construction details" hint="Select every element the garment actually has — each affects the final commercial estimate." />
            {modifiers.length === 0 ? <EmptyHint what="construction modifiers" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {modifiers.map((m) => {
                  const selected = isModSelected(m.key);
                  const entry = (inputs.construction.modifiers || []).find((x) => x.key === m.key);
                  return (
                    <div key={m.key} className={`border p-3 transition-colors ${selected ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5]"}`}>
                      <button type="button" onClick={() => toggleModifier(m.key)} className="w-full flex items-center gap-3 text-left"
                              data-testid={`mms-mod-${m.key}`}>
                        <span className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center text-[10px] ${selected ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`}>{selected ? "✓" : ""}</span>
                        <span className="text-xs uppercase tracking-widest flex-1 truncate">{m.label}</span>
                      </button>
                      {selected && !m.is_range && (
                        <div className="mt-2 flex items-center gap-2 pl-7">
                          <span className="text-[10px] uppercase tracking-widest text-[#737373]">count</span>
                          <input type="number" min="1" className="input-dark w-16 text-xs py-1"
                                 value={entry?.count || 1} onChange={(e) => setModifierCount(m.key, e.target.value)}
                                 data-testid={`mms-mod-count-${m.key}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* 4. Materials */}
          <Card data-testid="mms-step-materials">
            <StepHeader number={4} title="Materials" hint="Different fabric families and combinations affect the commercial price." />
            <div className="mb-4">
              <FieldLabel>Material family</FieldLabel>
              {wrap("materials.handling_key",
                <select className="input-dark w-full" value={inputs.materials.handling_key}
                        onChange={(e) => editWithConfirm("materials.handling_key", e.target.value)}
                        data-testid="mms-material-handling">
                  <option value="">— Select —</option>
                  {materialHandling.map((h) => (
                    <option key={h.key} value={h.key}>{h.label}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!inputs.materials.multiple_materials}
                       onChange={(e) => patch("materials.multiple_materials", e.target.checked)}
                       data-testid="mms-multiple-materials" />
                <span>Multiple materials on the same garment</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Materials cost estimate — min (€)</FieldLabel>
                <input type="number" step="any" className="input-dark w-full"
                       value={inputs.materials.cost_range?.min || 0}
                       onChange={(e) => patch("materials.cost_range.min", Number(e.target.value))}
                       data-testid="mms-material-cost-min" />
              </div>
              <div>
                <FieldLabel>Materials cost estimate — max (€)</FieldLabel>
                <input type="number" step="any" className="input-dark w-full"
                       value={inputs.materials.cost_range?.max || 0}
                       onChange={(e) => patch("materials.cost_range.max", Number(e.target.value))}
                       data-testid="mms-material-cost-max" />
              </div>
            </div>
          </Card>

          {/* 5. Manual work + processing */}
          <Card data-testid="mms-step-work">
            <StepHeader number={5} title="Manual work & processing" hint="Applications, patchwork, prints, embroidery, treatments." />
            {manualBrackets.length > 0 && (
              <>
                <FieldLabel>Manual work brackets</FieldLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                  {manualBrackets.map((b) => (
                    <button key={b.key} type="button" onClick={() => toggleManual(b.key)}
                            className={`text-left border p-3 ${isManualSelected(b.key) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                            data-testid={`mms-manual-${b.key}`}>
                      <div className="text-xs uppercase tracking-widest">{b.label}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <FieldLabel>Processing techniques</FieldLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {processing.map((t) => (
                <button key={t.key} type="button" onClick={() => toggleProc(t.key)}
                        className={`text-left border p-3 ${isProcSelected(t.key) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`}
                        data-testid={`mms-proc-${t.key}`}>
                  <span className="text-xs uppercase tracking-widest">{t.label}{t.external ? " *" : ""}</span>
                </button>
              ))}
            </div>
            {processing.some((t) => t.external && isProcSelected(t.key)) && (
              <div className="text-[10px] text-[#737373] mt-2">* External process — MMS will confirm final cost with the supplier.</div>
            )}
          </Card>

          {/* 6. Production */}
          <Card data-testid="mms-step-production">
            <StepHeader number={6} title="Production" hint="Larger runs unlock better per-piece pricing." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Quantity (pieces)</FieldLabel>
                <input type="number" min="1" className="input-dark w-full"
                       value={inputs.production.quantity}
                       onChange={(e) => patch("production.quantity", Number(e.target.value))}
                       data-testid="mms-prod-quantity" />
              </div>
              <div>
                <FieldLabel>Notes for MMS Studio</FieldLabel>
                <textarea className="input-dark w-full" rows={2} value={inputs.notes || ""}
                          onChange={(e) => patch("notes", e.target.value)}
                          data-testid="mms-notes"
                          placeholder="Anything the MMS team should know…" />
              </div>
            </div>
          </Card>
        </div>

        {/* Estimate + saved sims */}
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <div className="card-dark p-6 md:p-8 mb-6" data-testid="mms-estimate-box">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} strokeWidth={1.5} />
                <div className="label-caps">AI ESTIMATE</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-4">Preventivo indicativo</div>
              {estimate ? (
                <>
                  <div className="py-3 border-b border-[#E5E5E5]">
                    <div className="flex items-baseline justify-between">
                      <div className="text-xs uppercase tracking-widest text-[#525252]">Campione</div>
                      <div className="font-serif text-2xl tabular-nums text-[#0A0A0A]" data-testid="est-sample-price">
                        {formatCurrency(estimate.sample_price, estimate.currency)}
                      </div>
                    </div>
                  </div>

                  {estimate.quantity > 0 && (
                    <div className="py-3 border-b border-[#E5E5E5]">
                      <div className="flex items-baseline justify-between">
                        <div className="text-xs uppercase tracking-widest text-[#525252]">
                          Produzione {estimate.quantity} {estimate.quantity === 1 ? "pezzo" : "pezzi"}
                        </div>
                        <div className="text-sm tabular-nums text-[#0A0A0A]" data-testid="est-per-unit">
                          {formatCurrency(estimate.production_price_per_unit, estimate.currency)} / pz
                        </div>
                      </div>
                      <div className="flex items-baseline justify-between mt-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Totale produzione</div>
                        <div className="text-sm tabular-nums text-[#0A0A0A]" data-testid="est-production-total">
                          {formatCurrency(estimate.production_total, estimate.currency)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4">
                    <div className="flex items-baseline justify-between">
                      <div className="label-caps">Prezzo indicativo</div>
                      <div className="font-serif text-xl tabular-nums" data-testid="est-total">
                        {formatCurrency(estimate.total, estimate.currency)}
                      </div>
                    </div>
                  </div>

                  {estimate.exclusions?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                      <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">Esclusi</div>
                      <div className="text-[11px] text-[#525252]" data-testid="est-exclusions">
                        {estimate.exclusions.join(" · ")}
                      </div>
                    </div>
                  )}

                  {confidenceLabel && (
                    <div className="mt-3 text-[10px] uppercase tracking-widest text-[#737373]">
                      AI status: <span className="text-[#0A0A0A]" data-testid="est-confidence-label">{confidenceLabel}</span>
                    </div>
                  )}
                </>
              ) : <div className="text-xs text-[#737373]">Enter details to see an estimate.</div>}

              {pendingConfirmations.length > 0 && (
                <div className="mt-4 border border-amber-400 bg-amber-50 p-3 text-[11px] text-amber-800" data-testid="mms-pending-confirmations">
                  {pendingConfirmations.length} AI-flagged field{pendingConfirmations.length > 1 ? "s" : ""} need your confirmation before requesting the official quote.
                </div>
              )}

              <div className="mt-6 space-y-2">
                <button onClick={doRequestQuote} disabled={busy || pendingConfirmations.length > 0}
                        className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="mms-request-quote">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  REQUEST OFFICIAL QUOTE
                </button>
                <button onClick={doSave} disabled={busy}
                        className="btn-ghost w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="mms-save-simulation">
                  <Save size={14} /> SAVE SIMULATION
                </button>
                {currentSimId && (
                  <button onClick={resetForm}
                          className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] w-full py-2 inline-flex items-center justify-center gap-2">
                    <RotateCcw size={11} /> Start a new simulation
                  </button>
                )}
              </div>
            </div>

            <div className="card-dark p-6" data-testid="mms-saved-list">
              <div className="flex items-center gap-2 mb-4"><FileText size={14} strokeWidth={1.5} /><div className="label-caps">Saved Simulations</div></div>
              {savedSims.length === 0 ? <div className="text-xs text-[#737373] italic">No simulations yet.</div> : (
                <ul className="space-y-2">
                  {savedSims.map((s) => (
                    <li key={s.id} className={`border p-3 ${currentSimId === s.id ? "border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid={`mms-saved-${s.id}`}>
                      <button type="button" className="w-full text-left" data-testid={`mms-saved-open-${s.id}`}
                              onClick={() => { setInputs({ ...blankInputs(), ...s.inputs }); setName(s.name || ""); setCurrentSimId(s.id); setConfidenceMap(s.inputs?.confidence || {}); setConfirmedFields(s.inputs?.confirmed_fields || []); setAnalysisToken(null); setParams({ sim: s.id }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                        <div className="text-xs text-[#0A0A0A] font-medium truncate">{s.name || "Untitled"}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-[#737373] uppercase tracking-widest">{s.status === "quote_requested" ? "Quote requested" : "Draft"}</span>
                          <span className="text-[11px] tabular-nums text-[#0A0A0A]">
                            {formatCurrency(s.estimate?.total || 0, s.currency || "EUR")}
                          </span>
                        </div>
                      </button>
                      <button onClick={() => removeSim(s.id)} className="mt-2 text-[10px] text-[#737373] hover:text-red-600 inline-flex items-center gap-1"
                              data-testid={`mms-saved-delete-${s.id}`}>
                        <Trash2 size={11} /> Delete
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
